import React from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { toArray, toPlainObject } from 'lodash';

import { STATIC_ROOT } from '../../../../constants';
import { IBAN_TYPE_RELATIVE } from '../../../../services/account/iban/constants';
import { PAYMENT_REQUEST_TYPE_IBAN } from '../../../../services/account/paymentrequest/request/constants';

import {
    sendPaymentRequest,
    reset,
    setTimeoutId
} from '../../../../services/account/paymentrequest/request/actions';
import { fetchIbanIfNeeded } from '../../../../services/account/iban/actions';

import { getValidatedAmount } from '../../../../services/account/gains/selector';
import { getOnGoingPaidAmount } from '../../../../services/account/paymentrequest/ongoing/selector';
import * as paymentRequestSelector from '../../../../services/account/paymentrequest/request/selector';

import { formatAmountCurrency } from '../../../../utils';
import { minusAmount, truncateAmount } from '../../../../utils/mathUtils';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import {
    FormComponent,
    NumberInputFieldComponent,
    LegendFieldComponent,
    RadioInputFieldComponent
} from '../../../../components/form';
import SvgComponent from '../../../SvgComponent';
import IbanFormComponent from '../myprofile/IbanFormComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';

import './PaymentRequestFormComponent.scss';

const FORM_NAME = 'paymentrequest-form-iban-relative';

class PaymentRequestPayToSomeone extends React.Component {
    constructor(props) {
        super(props);
        this.state = { showForm: false };
    }

    componentDidMount() {
        this.props.fetchIbanIfNeeded();
    }

    componentDidUpdate(prevProps) {
        const { createIbanSuccess } = this.props;

        if (!prevProps.createIbanSuccess && createIbanSuccess) {
            this.setState({ showForm: false });
        }
    }

    submit = formData => {
        const { ibans, sendPaymentRequest, timeoutId } = this.props;

        clearTimeout(timeoutId);

        const account = ibans.filter(i => i.id === Number(formData.ibanId))[0];

        if (account) {
            const body = {
                requestedAmount: formData.requestedAmount,
                lastname: account.lastname,
                firstname: account.firstname,
                iban: account.iban,
                bic: account.bic,
                thirdPartyIban: true
            };
            sendPaymentRequest(PAYMENT_REQUEST_TYPE_IBAN, body);
        }
    };

    renderIbanLabel = iban => {
        const { i18n } = this.props;

        if (iban.label) {
            return iban.label;
        } else if (iban.firstname || iban.lastname) {
            const name = `${iban.firstname ? iban.firstname : ''} ${
                iban.lastname ? iban.lastname : ''
            }`;
            return convertHtmlToReactDomWithParams(i18n.ACCOUNT_OF, name);
        } else {
            return iban.iban;
        }
    };

    populate() {
        const { i18n, ibans } = this.props;

        return ibans
            ? toArray(
                  ibans
                      .filter(iban => iban.type === IBAN_TYPE_RELATIVE)
                      .map((iban, i) => ({
                          label: (
                              <div className="account">
                                  <div className="text">
                                      <p className="label">{this.renderIbanLabel(iban)}</p>
                                      <p>
                                          {convertHtmlToReactDomWithParams(
                                              i18n.IBAN_NUMBER,
                                              iban.iban
                                          )}
                                      </p>
                                      {iban.bic && (
                                          <p>
                                              {convertHtmlToReactDomWithParams(
                                                  i18n.BIC_CODE,
                                                  iban.bic
                                              )}
                                          </p>
                                      )}
                                  </div>
                              </div>
                          ),
                          value: iban.id
                      }))
              )
            : [];
    }

    handleClick = () => {
        this.setState({
            showForm: !this.state.showForm
        });
    };

    handleKeyDown = e => {
        if (e.key === 'Enter') {
            this.handleClick();
        }
    };

    render() {
        const {
            i18n,
            i18nCommon,
            i18nForm,
            i18nIban,
            requestSubmit,
            errorSubmit,
            errorMessage,
            validatedCashbackAmount,
            paymentRequestTotalAmount,
            requestedAmount,
            ibanFetching
        } = this.props;

        const validatedMinusPaymentRequest = truncateAmount(
            minusAmount(validatedCashbackAmount, paymentRequestTotalAmount)
        );
        const remainingJackpotAfterPayment =
            minusAmount(validatedMinusPaymentRequest, requestedAmount) < 0
                ? 0
                : minusAmount(validatedMinusPaymentRequest, requestedAmount);

        return (
            <div className="payment-request">
                <p className="resume">{i18n.AMOUNT_TO_SOMEONE_ACCOUNT}</p>

                <div className="account-button">
                    <span
                        id="add-account-button"
                        title={i18nIban.ADD_NEW_ACCOUNT}
                        aria-label={i18nIban.ADD_NEW_ACCOUNT}
                        aria-expanded={this.state.showForm}
                        role="button"
                        tabIndex="0"
                        onClick={this.handleClick}
                        onKeyDown={this.handleKeyDown}>
                        <span className="plus">+</span>
                        <span className="text">{i18nIban.ADD_NEW_ACCOUNT}</span>
                    </span>
                </div>
                {this.state.showForm && (
                    <React.Fragment>
                        <p className="new-account">{i18nIban.NEW_ACCOUNT}</p>
                        <IbanFormComponent type={IBAN_TYPE_RELATIVE} />
                    </React.Fragment>
                )}

                <FormComponent
                    form={FORM_NAME}
                    classNameInputContainer="payment-request-form"
                    onSubmit={this.submit}
                    requestSubmit={requestSubmit}
                    errorSubmit={errorSubmit}
                    submitText={i18n.PAYMENT_REQUEST_BUTTON}>
                    <React.Fragment>
                        {ibanFetching ? (
                            <LoadingComponent />
                        ) : (
                            <RadioInputFieldComponent
                                name="ibanId"
                                options={this.populate()}
                                isRequired
                                i18nFeatureForm={i18nForm}
                            />
                        )}
                    </React.Fragment>

                    <NumberInputFieldComponent
                        name="requestedAmount"
                        label={i18n.AMOUNT_TO_ACCOUNT}
                        placeholder={i18n.EXAMPLE_AMOUNT}
                        classNameInputContainer="amount"
                        min={0.1}
                        max={validatedMinusPaymentRequest}
                        isRequired
                        hideRequired
                        isEuro
                        showSymbol={'€'}
                    />
                    <React.Fragment>
                        <div className="information-div">
                            <span>
                                <SvgComponent
                                    src={`${STATIC_ROOT}/information.svg`}
                                    width="20"
                                    height="20"
                                    aria-hidden="true"
                                    focusable="false"
                                />
                            </span>
                            <span>
                                {convertHtmlToReactDomWithParams(
                                    i18nCommon.REMAINING_JACKPOT_AFTER_PAYMENT_INDICATION,
                                    formatAmountCurrency(remainingJackpotAfterPayment)
                                )}
                            </span>
                        </div>
                    </React.Fragment>

                    <React.Fragment>
                        {errorMessage && (
                            <LegendFieldComponent classNameInputContainer="color-err">
                                {errorMessage}
                            </LegendFieldComponent>
                        )}
                    </React.Fragment>
                </FormComponent>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const user = toPlainObject(state.account.user);
    const validatedCashbackAmount = getValidatedAmount(state);
    const paymentRequestTotalAmount = getOnGoingPaidAmount(state);
    const requestedAmount = formValueSelector(FORM_NAME)(state, 'requestedAmount') || 0;

    return {
        i18n: state.i18n.page.ACCOUNT.CASHBACK_JACKPOT.PAYMENT_REQUEST_FORM.OTHER_ACCOUNT,
        i18nCommon: state.i18n.page.ACCOUNT.CASHBACK_JACKPOT.PAYMENT_REQUEST_FORM,
        i18nForm: state.i18n.feature.form,
        i18nIban: state.i18n.page.ACCOUNT.PROFILE.IBAN,
        user: user,
        ibans: state.account.iban.ibans,
        ibanFetching: state.account.iban.fetching,
        requestSubmit: paymentRequestSelector.isRequesting(state),
        errorSubmit: paymentRequestSelector.isError(state),
        errorMessage: paymentRequestSelector.getError(state),
        validatedCashbackAmount: validatedCashbackAmount,
        paymentRequestTotalAmount: paymentRequestTotalAmount,
        requestedAmount: requestedAmount,
        timeoutId: paymentRequestSelector.getTimeoutId(state),
        createIbanSuccess: state.account.iban.createIbanSuccess
    };
};

const mapDispatchToProps = {
    fetchIbanIfNeeded,
    sendPaymentRequest,
    reset,
    setTimeoutId
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentRequestPayToSomeone);



// WEBPACK FOOTER //
// ./src/components/pages/account/cashback/PaymentRequestPayToSomeone.js